import React, { useState, useEffect } from 'react';
import { 
  Users, Crown, Gift, Menu, Zap, Bot, BookOpen, GraduationCap,
  UserX, Loader, CheckCircle, AlertCircle, UserPlus
} from 'lucide-react';
import { useUsers } from './hooks/useUsers';
import { useSubscription } from './hooks/useSubscription';
import { useMaterials } from './hooks/useMaterials';
import UserTable from './components/UserTable';
import UserCard from './components/UserCard';
import SubscriptionModal from './components/SubscriptionModal';
import { formatDateForInput } from './utils/formatters';
import GiftManager from './GiftManager';
import UserModal from './UserModal';
import AccessManager from './AccessManager';
import SearchAndFilters from '../SearchAndFilters';

const AdminPanel = () => {
  // Основные хуки
  const {
    users,
    filteredUsers,
    setFilteredUsers,
    loading,
    notification,
    fetchUsers,
    updateUser,
    deleteUser,
    applyFilters,
    getUserTrainingsList,
    getUserCoursesList,
    showNotification
  } = useUsers();

  const {
    subscriptionState,
    setSubscriptionType,
    setSubscriptionDuration,
    setCustomStartDate,
    setCustomEndDate,
    setShowDatePicker,
    openSubscriptionModal,
    closeSubscriptionModal,
    handleManageSubscription
  } = useSubscription(users, setFilteredUsers, setFilteredUsers, showNotification);

  const {
    availableTrainings,
    availableCourses,
    trainingsMap,
    coursesMap,
    expandedMaterials,
    toggleMaterialsExpand,
    getTrainingAccess,
    getCourseAccess,
    getTrainingTitle,
    getCourseTitle,
    loadTrainingsAndCourses
  } = useMaterials();

  // Локальные состояния
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTrainingId, setSelectedTrainingId] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  const [selectedUserForGift, setSelectedUserForGift] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Подсчет статистики
  const premiumCount = users.filter(u => u.premium === true).length;
  const freeCount = users.filter(u => u.premium !== true).length;
  const manualCount = users.filter(u => u.premium === true && u.premiumActivationType === 'manual').length;
  const autoCount = users.filter(u => u.premium === true && u.premiumActivationType === 'auto').length;
  const withTrainingsCount = users.filter(u => getUserTrainingsList(u).length > 0).length;
  const withCoursesCount = users.filter(u => getUserCoursesList(u).length > 0).length;
  const telegramCount = users.filter(u => u.userType === 'telegram').length;
  const webCount = users.filter(u => u.userType === 'web').length;

  // Обработчики модальных окон
  const openAccessModal = (user) => {
    setSelectedUser(user);
    setIsAccessModalOpen(true);
  };

  const openUserModal = (user, editMode = false) => {
    setSelectedUser(user);
    setIsEditMode(editMode);
    setIsUserModalOpen(true);
  };

  const openGiftModal = (user) => {
    setSelectedUserForGift(user);
    setIsGiftModalOpen(true);
  };

  const handleAccessUpdated = () => {
    fetchUsers(true);
    showNotification('success', 'Доступ к материалам обновлен');
  };

  const handleGiftIssued = (count) => {
    showNotification('success', `Подарок успешно выдан (${count} шт.)`);
  };

  const handleUpdateUser = async (userId, formData) => {
    const updateData = {
      fullName: formData.fullName,
      phone: formData.phone,
      email: formData.email,
      refBonus: Number(formData.refBonus) || 0,
      birthDate: formData.birthDate || null,
      savedAddress: {
        street: formData.savedAddress || '',
        deliveryInstructions: formData.deliveryInstructions || ''
      },
      updatedAt: new Date().toISOString()
    };

    const success = await updateUser(userId, updateData);
    if (success) {
      setIsEditMode(false);
      if (selectedUser?.id === userId) {
        setSelectedUser(prev => ({ ...prev, ...updateData }));
      }
    }
  };

  // Поиск
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      showNotification('warning', 'Введите поисковый запрос');
      return;
    }

    setSearchLoading(true);
    setIsSearchMode(true);
    
    try {
      const { collection, getDoc, doc, getDocs, query, where } = await import("firebase/firestore");
      const { db } = await import('../../firebase/firestore');
      
      const cleanQuery = searchQuery.trim().toLowerCase();
      const foundUsers = [];
      const userIdsSet = new Set();

      // Поиск по Telegram пользователям
      try {
        // Поиск по ID
        const telegramDoc = await getDoc(doc(db, "telegramUsers", cleanQuery));
        if (telegramDoc.exists()) {
          const userId = telegramDoc.id;
          if (!userIdsSet.has(userId)) {
            userIdsSet.add(userId);
            foundUsers.push({ id: userId, userType: 'telegram', ...telegramDoc.data() });
          }
        }
        
        // Поиск по другим полям в telegramUsers
        const telegramUsersRef = collection(db, "telegramUsers");
        const searchVariants = [cleanQuery, cleanQuery.charAt(0).toUpperCase() + cleanQuery.slice(1)];
        
        for (const variant of searchVariants) {
          const fields = ['username', 'fullName', 'phone', 'email'];
          
          for (const field of fields) {
            const fieldQuery = query(
              telegramUsersRef, 
              where(field, ">=", variant), 
              where(field, "<=", variant + '\uf8ff')
            );
            const snapshot = await getDocs(fieldQuery);
            snapshot.forEach(doc => {
              const userId = doc.id;
              if (!userIdsSet.has(userId)) {
                userIdsSet.add(userId);
                foundUsers.push({ id: userId, userType: 'telegram', ...doc.data() });
              }
            });
          }
        }
      } catch (error) {
        console.log("Ошибка поиска в Telegram:", error);
      }

      // Поиск по Web пользователям
      try {
        const webUsersRef = collection(db, "webUsers");
        
        // Поиск по ID
        const webDoc = await getDoc(doc(db, "webUsers", cleanQuery));
        if (webDoc.exists()) {
          const userId = webDoc.id;
          if (!userIdsSet.has(userId)) {
            userIdsSet.add(userId);
            foundUsers.push({ id: userId, userType: 'web', ...webDoc.data() });
          }
        }
        
        // Поиск по email и fullName
        const searchVariants = [cleanQuery, cleanQuery.charAt(0).toUpperCase() + cleanQuery.slice(1)];
        
        for (const variant of searchVariants) {
          const fields = ['email', 'fullName', 'phone'];
          
          for (const field of fields) {
            const fieldQuery = query(
              webUsersRef, 
              where(field, ">=", variant), 
              where(field, "<=", variant + '\uf8ff')
            );
            const snapshot = await getDocs(fieldQuery);
            snapshot.forEach(doc => {
              const userId = doc.id;
              if (!userIdsSet.has(userId)) {
                userIdsSet.add(userId);
                foundUsers.push({ id: userId, userType: 'web', ...doc.data() });
              }
            });
          }
        }
      } catch (error) {
        console.log("Ошибка поиска в Web:", error);
      }

      if (foundUsers.length > 0) {
        const formattedUsers = foundUsers.map(userData => ({
          id: userData.id,
          userType: userData.userType,
          username: userData.username || null,
          fullName: userData.fullName || null,
          firstName: userData.firstName || null,
          lastName: userData.lastName || null,
          phone: userData.phone || null,
          email: userData.email || null,
          premium: userData.premium || false,
          premiumExpiresAt: userData.premiumExpiresAt || null,
          premiumActivatedAt: userData.premiumActivatedAt || null,
          premiumActivationType: userData.premiumActivationType || null,
          subscription: userData.subscription || null,
          refBonus: userData.refBonus || 0,
          birthDate: userData.birthDate || null,
          savedAddress: userData.savedAddress || null,
          joinedAt: userData.joinedAt || new Date().toISOString(),
          trainings: userData.trainings || [],
          courses: userData.courses || [],
          trainingsAccess: userData.trainingsAccess || [],
          coursesAccess: userData.coursesAccess || []
        }));
        
        setSearchResults(formattedUsers);
        showNotification('success', `Найдено пользователей: ${formattedUsers.length}`);
      } else {
        setSearchResults([]);
        showNotification('error', 'Пользователи не найдены');
      }
      
    } catch (error) {
      console.error("Ошибка поиска:", error);
      showNotification('error', `Ошибка при поиске: ${error.message}`);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const clearSearch = () => {
    setIsSearchMode(false);
    setSearchQuery('');
    setSearchResults([]);
    applyFilters(users, activeFilter, selectedTrainingId, selectedCourseId);
  };

  const displayUsers = isSearchMode ? searchResults : filteredUsers;
  const displayLoading = isSearchMode ? searchLoading : loading;

  // Эффекты
  useEffect(() => {
    fetchUsers();
    loadTrainingsAndCourses();
  }, []);

  useEffect(() => {
    if (!isSearchMode) {
      applyFilters(users, activeFilter, selectedTrainingId, selectedCourseId);
    }
  }, [activeFilter, users, isSearchMode, selectedTrainingId, selectedCourseId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden">
      <div className="h-screen flex flex-col">
        {/* Уведомления */}
        {notification.message && (
          <div className={`fixed top-4 left-4 right-4 md:left-auto md:right-4 z-[200] flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg max-w-md animate-slide-in ${
            notification.type === 'success' 
              ? 'bg-green-900/30 text-green-300 border border-green-700' 
              : notification.type === 'error'
              ? 'bg-red-900/30 text-red-300 border border-red-700'
              : 'bg-yellow-900/30 text-yellow-300 border border-yellow-700'
          }`}>
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
        )}

        {/* Верхняя панель */}
        <div className="flex-shrink-0 bg-gray-900/50 backdrop-blur-sm border-b border-gray-700 relative z-20">
          <div className="p-4 md:p-6">
            <div className="md:hidden">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-xl font-bold text-white">Управление пользователями</h1>
                </div>
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="p-2 rounded-lg bg-gray-800 shadow-sm hover:bg-gray-700 transition-colors"
                >
                  <Menu className="w-5 h-5 text-gray-300" />
                </button>
              </div>

              {showMobileMenu && (
                <div className="bg-gray-800 rounded-xl shadow-sm p-4 mb-4 animate-slide-down border border-gray-700">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 bg-gray-700/50 px-3 py-2 rounded-lg">
                      <Users className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-400">Всего</p>
                        <p className="font-semibold text-white">{users.length}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-gray-700/50 px-3 py-2 rounded-lg">
                      <Crown className="w-4 h-4 text-yellow-400" />
                      <div>
                        <p className="text-xs text-gray-400">Premium</p>
                        <p className="font-semibold text-white">{premiumCount}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-gray-700/50 px-3 py-2 rounded-lg">
                      <Users className="w-4 h-4 text-blue-400" />
                      <div>
                        <p className="text-xs text-gray-400">Telegram</p>
                        <p className="font-semibold text-white">{telegramCount}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-gray-700/50 px-3 py-2 rounded-lg">
                      <Users className="w-4 h-4 text-green-400" />
                      <div>
                        <p className="text-xs text-gray-400">Web</p>
                        <p className="font-semibold text-white">{webCount}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="hidden md:block">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white">Управление клубом</h1>
                  <p className="text-gray-400 mt-1">Админ-панель для управления доступом к Premium, подаркам и материалам</p>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-lg shadow-sm border border-gray-700">
                    <Users className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-300 font-medium">{users.length} пользователей</span>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-lg shadow-sm border border-gray-700">
                    <Crown className="w-5 h-5 text-yellow-400" />
                    <span className="text-gray-300 font-medium">{premiumCount} Premium</span>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-900/30 px-4 py-2 rounded-lg shadow-sm border border-blue-700">
                    <Users className="w-5 h-5 text-blue-400" />
                    <span className="text-gray-300 font-medium">📱 {telegramCount}</span>
                  </div>
                  <div className="flex items-center gap-2 bg-green-900/30 px-4 py-2 rounded-lg shadow-sm border border-green-700">
                    <Users className="w-5 h-5 text-green-400" />
                    <span className="text-gray-300 font-medium">🌐 {webCount}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 pb-4">
            <SearchAndFilters
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              onSearch={handleSearch}
              searchLoading={searchLoading}
              isSearchMode={isSearchMode}
              onClearSearch={clearSearch}
              searchResultsCount={displayUsers.length}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              onRefresh={() => fetchUsers(true)}
              loading={loading}
              totalUsersCount={users.length}
              premiumCount={premiumCount}
              manualCount={manualCount}
              autoCount={autoCount}
              freeCount={freeCount}
              telegramCount={telegramCount}
              webCount={webCount}
              withTrainingsCount={withTrainingsCount}
              withCoursesCount={withCoursesCount}
              selectedTrainingId={selectedTrainingId}
              onTrainingFilterChange={setSelectedTrainingId}
              selectedCourseId={selectedCourseId}
              onCourseFilterChange={setSelectedCourseId}
              availableTrainings={availableTrainings}
              availableCourses={availableCourses}
            />
          </div>
        </div>

        {/* Основной контент */}
        <div className="flex-1 overflow-hidden px-4 md:px-6 pb-4 relative z-10">
          <div className="h-full overflow-y-auto">
            <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden h-full flex flex-col border border-gray-700">
              {displayLoading ? (
                <div className="flex flex-col items-center justify-center py-20 flex-1">
                  <Loader className="w-8 h-8 text-blue-500 animate-spin mb-4" />
                  <p className="text-gray-400">{isSearchMode ? 'Поиск пользователей...' : 'Загрузка пользователей...'}</p>
                </div>
              ) : displayUsers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 flex-1">
                  {isSearchMode ? (
                    <>
                      <UserX className="w-16 h-16 text-gray-600 mb-4" />
                      <h3 className="text-lg font-medium text-gray-300 mb-2">Пользователи не найдены</h3>
                      <button onClick={clearSearch} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                        Показать всех пользователей
                      </button>
                    </>
                  ) : (
                    <>
                      <Users className="w-16 h-16 text-gray-600 mb-4" />
                      <h3 className="text-lg font-medium text-gray-300 mb-2">Пользователи не найдены</h3>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <UserTable 
                    users={displayUsers}
                    onView={openUserModal}
                    onEdit={(user) => openUserModal(user, true)}
                    onGift={openGiftModal}
                    onAccess={openAccessModal}
                    onSubscribe={openSubscriptionModal}
                    onRevoke={(user) => openSubscriptionModal(user, 'revoke')}
                    onDelete={deleteUser}
                    getUserTrainingsList={getUserTrainingsList}
                    getUserCoursesList={getUserCoursesList}
                    trainingsMap={trainingsMap}
                    coursesMap={coursesMap}
                    expandedMaterials={expandedMaterials}
                    toggleMaterialsExpand={toggleMaterialsExpand}
                    getTrainingAccess={getTrainingAccess}
                    getCourseAccess={getCourseAccess}
                  />
                  
                  <div className="md:hidden overflow-auto flex-1">
                    <div className="p-2 space-y-3">
                      {displayUsers.map((user) => (
                        <UserCard 
                          key={user.id}
                          user={user}
                          onView={openUserModal}
                          onEdit={(user) => openUserModal(user, true)}
                          onGift={openGiftModal}
                          onAccess={openAccessModal}
                          onSubscribe={openSubscriptionModal}
                          onRevoke={(user) => openSubscriptionModal(user, 'revoke')}
                          onDelete={deleteUser}
                          getUserTrainingsList={getUserTrainingsList}
                          getUserCoursesList={getUserCoursesList}
                          trainingsMap={trainingsMap}
                          coursesMap={coursesMap}
                          expandedMaterials={expandedMaterials}
                          toggleMaterialsExpand={toggleMaterialsExpand}
                          getTrainingAccess={getTrainingAccess}
                          getCourseAccess={getCourseAccess}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-700 py-3 px-4 md:px-6 bg-gray-900/50">
          <div className="text-center text-gray-500 text-xs">
            <p className="text-gray-400">Админ-панель управления Premium доступом • {new Date().getFullYear()}</p>
            <p className="mt-1 text-gray-500">Всего: {users.length} • Premium: {premiumCount} • Ручные: {manualCount} • Авто: {autoCount} • Telegram: {telegramCount} • Web: {webCount}</p>
          </div>
        </div>
      </div>

      {/* Модальные окна */}
      <UserModal 
        isOpen={isUserModalOpen} 
        onClose={() => setIsUserModalOpen(false)} 
        user={selectedUser} 
        isEditMode={isEditMode} 
        onEditModeChange={setIsEditMode} 
        onUpdate={handleUpdateUser} 
        onDelete={deleteUser} 
        onOpenGiftModal={openGiftModal} 
        onOpenSubscriptionModal={openSubscriptionModal} 
      />

      <AccessManager
        isOpen={isAccessModalOpen}
        onClose={() => setIsAccessModalOpen(false)}
        user={selectedUser}
        availableTrainings={availableTrainings}
        availableCourses={availableCourses}
        trainingsMap={trainingsMap}
        coursesMap={coursesMap}
        onAccessUpdated={handleAccessUpdated}
      />

      <SubscriptionModal
        isOpen={subscriptionState.isSubscriptionModalOpen}
        selectedUser={subscriptionState.selectedUser}
        subscriptionAction={subscriptionState.subscriptionAction}
        subscriptionType={subscriptionState.subscriptionType}
        subscriptionDuration={subscriptionState.subscriptionDuration}
        customStartDate={subscriptionState.customStartDate}
        customEndDate={subscriptionState.customEndDate}
        showDatePicker={subscriptionState.showDatePicker}
        loading={subscriptionState.loading}
        onClose={closeSubscriptionModal}
        onSetSubscriptionType={setSubscriptionType}
        onSetSubscriptionDuration={setSubscriptionDuration}
        onSetCustomStartDate={setCustomStartDate}
        onSetCustomEndDate={setCustomEndDate}
        onSetShowDatePicker={setShowDatePicker}
        onSubmit={handleManageSubscription}
        formatDateForInput={formatDateForInput}
      />

      <GiftManager 
        isOpen={isGiftModalOpen} 
        onClose={() => setIsGiftModalOpen(false)} 
        user={selectedUserForGift} 
        onGiftIssued={handleGiftIssued} 
      />
    </div>
  );
};

export default AdminPanel;